import { useEffect, useMemo, useRef } from 'react';
import { Trans } from '@lingui/macro';
import MenuItem from '@material-ui/core/MenuItem';
import { useFormContext } from 'react-hook-form';
import { isEmpty, isEqual } from 'lodash';
import HelpOutline from '@material-ui/icons/HelpOutline';
import { makeStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import NgpSelect from '../../../../form/NgpSelect';
import NgpToggle from '../../../../form/NgpToggle';
import useNgpFormContext from '../../../../../utilities/NgpFormContextProviderUtilities';
import { useTestTypeContext } from '../hooks/UseTestTypeContext';
import { StyledTooltip } from '../../../../panels/net-test-now-panels/NetTestNowAgentTagSelect';
import NgpRow from '../../../../form/NgpRow';
import {
    BEACON_MODES,
    computeInitialBeaconMode,
    getAvailableBeaconModes,
    getBeaconModeLabel,
    resolveBeaconSteps,
} from './SmartEdgeHelpers';
import NgpHiddenField from '../../../../form/NgpHiddenField';
import { usePaddingLeftStyles } from '../../../../form/FormStyles';
import { SHOULD_DIRTY_TRUE } from '../../ServiceTestsFormControlConstants';
import TestFormSection from './TestFormSection/TestFormSection';

export const useStyles = makeStyles((theme) => ({
    row: {
        alignItems: 'center',
        flexWrap: 'wrap',
        rowGap: theme.spacing(1),
        columnGap: theme.spacing(1.5),
        '& .MuiFormControlLabel-root': {
            marginTop: 0,
            marginRight: 0,
            marginLeft: 0,
        },
        '& .MuiTextField-root': {
            marginTop: 0,
            marginRight: 0,
        },
        '& .MuiFormControlLabel-label': {
            color: theme.palette.text.primary,
        },
    },
}));

const BEACON_MODE = 'beaconMode';
const BEACON_STEPS = 'beaconSteps';

const EnablePerStepsTable = ({ beaconSteps }) => {
    const paddingLeftStyles = usePaddingLeftStyles();
    return (
        <>
            <Box mt={3} />
            <div className={paddingLeftStyles.ngpFormFields}>
                <table>
                    <tbody>
                        {beaconSteps.map((step, index) => (
                            <tr key={step.metricId || index}>
                                <td>
                                    <NgpToggle
                                        fieldName={`beaconSteps.${index}.enabled`}
                                        label={step.metricTitle || step.metricName}
                                    />
                                    <NgpHiddenField
                                        fieldName={`beaconSteps.${index}.metricName`}
                                        defaultValue={step.metricName || ''}
                                    />
                                    <NgpHiddenField
                                        fieldName={`beaconSteps.${index}.metricTitle`}
                                        defaultValue={step.metricTitle || ''}
                                    />
                                    <NgpHiddenField
                                        fieldName={`beaconSteps.${index}.metricId`}
                                        defaultValue={step.metricId || ''}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

// store Previous Not Empty And Not Database Beacon Steps, for restore back STEP mode from other modes
// Which means:
//     1: for user do some changes with STEP(Enable Per Step) mode, but not saved,
//     2: and then swith to other modes,
//     3: and then swith back  STEP(Enable Per Step) mode,
//     we would restore use last changes
export const usePreviousNotEmptyAndNotDatabaseBeaconStepsArray = (lastBeaconSteps) => {
    const ref = useRef([]);
    useEffect(() => {
        if (!isEmpty(lastBeaconSteps)) {
            const firstBeaconStep = lastBeaconSteps[0];
            // Ensure that not from old database, just from UI changes
            if (!firstBeaconStep._id) {
                // Just for not same with enabled, like other fields change(like name, description or other fields change) changed no need do change here
                if (
                    !isEqual(
                        lastBeaconSteps?.map((step) => step?.enabled),
                        ref.current?.map((step) => step?.enabled)
                    )
                ) {
                    ref.current = lastBeaconSteps;
                }
            }
        }
    }, [lastBeaconSteps]);
    return ref.current;
};

const TestSmartEdgeMonitoringSection = () => {
    const classes = useStyles();
    const { formData } = useNgpFormContext();
    const { testType, isBttTest } = useTestTypeContext();
    const { watch, setValue } = useFormContext();

    const beaconSteps = useMemo(
        () => resolveBeaconSteps(formData, testType, isBttTest),
        [formData, testType, isBttTest]
    );

    const availableModes = useMemo(() => getAvailableBeaconModes(isBttTest, beaconSteps), [isBttTest, beaconSteps]);

    // Compute and set initial beaconMode when form data loads
    useEffect(() => {
        if (isEmpty(formData)) {
            return;
        }
        const initialMode = computeInitialBeaconMode(formData, beaconSteps);
        setValue(BEACON_MODE, initialMode);

        // Also register beaconSteps into the form so they are available at save time
        if (beaconSteps.length > 0) {
            setValue(BEACON_STEPS, beaconSteps);
        }
    }, [formData, beaconSteps, setValue]);

    const beaconMode = watch(BEACON_MODE) || BEACON_MODES.DISABLED;
    const lastBeaconSteps = watch(BEACON_STEPS);
    const previousNotEmptyBeaconSteps = usePreviousNotEmptyAndNotDatabaseBeaconStepsArray(lastBeaconSteps);
    const showStepsTable = beaconMode === BEACON_MODES.STEP && beaconSteps.length > 0;

    // Restore previous beacon steps if user switches back to STEP mode and
    // current beacon steps is empty (can happen when switching modes multiple times)
    // or current beacon steps set back by database values, firstBeaconStep with _id
    useEffect(() => {
        if (beaconMode === BEACON_MODES.STEP && !isEmpty(previousNotEmptyBeaconSteps)) {
            if (isEmpty(lastBeaconSteps)) {
                beaconSteps.forEach((_, index) => {
                    const enabled = previousNotEmptyBeaconSteps[index]?.enabled || false;
                    setValue(`beaconSteps.${index}.enabled`, enabled, SHOULD_DIRTY_TRUE);
                });
            } else {
                const firstBeaconStep = lastBeaconSteps[0];
                // Ensure that not from old database, just from UI changes
                if (firstBeaconStep._id) {
                    beaconSteps.forEach((_, index) => {
                        const enabled = previousNotEmptyBeaconSteps[index]?.enabled || false;
                        setValue(`beaconSteps.${index}.enabled`, enabled, SHOULD_DIRTY_TRUE);
                    });
                }
            }
        }
    }, [beaconMode, beaconSteps, lastBeaconSteps, previousNotEmptyBeaconSteps, setValue]);

    // Handle mode change side-effects.
    // onChangeFunc from NgpSelect receives the DOM event, not the value directly.
    const handleModeChange = (e) => {
        const newMode = e.target.value;
        if (newMode === BEACON_MODES.STEP && isEmpty(previousNotEmptyBeaconSteps)) {
            // Disable all steps when switching away from 'step' mode
            beaconSteps.forEach((_, index) => {
                setValue(`beaconSteps.${index}.enabled`, false, SHOULD_DIRTY_TRUE);
            });
        }
    };

    return (
        <TestFormSection title={<Trans>Smart Edge Monitoring</Trans>} addSpacer>
            <NgpRow className={classes.row}>
                <NgpSelect fieldName="beaconMode" onChangeFunc={handleModeChange}>
                    {availableModes.map((mode) => (
                        <MenuItem key={mode} value={mode}>
                            {getBeaconModeLabel(mode)}
                        </MenuItem>
                    ))}
                </NgpSelect>
                <StyledTooltip placement="right" title={<Trans>Allow ASI monitoring for Service Tests</Trans>}>
                    <HelpOutline fontSize="small" />
                </StyledTooltip>
            </NgpRow>

            {showStepsTable && <EnablePerStepsTable beaconSteps={beaconSteps} />}
        </TestFormSection>
    );
};

export default TestSmartEdgeMonitoringSection;
